// @flow

import React, { type ElementConfig, type ElementRef } from 'react';
import Hls from 'hls.js';
import { track } from 'app/common/lib/react/analytics';
import { Video } from 'gestalt';
import type { HlsConfig, Source } from 'app/packages/pinterest-flow-types/video';

// Video wrapper accepts all the same props you would normally pass to gestalt/Video,
// and also an additional HlsConfig.
type Props = {|
  ...ElementConfig<typeof Video>,
  hlsConfig?: HlsConfig,
  setVideoRef?: (ref: ElementRef<typeof Video> | null) => void,
|};

type State = {|
  canPlayVideo: boolean,
  isManifestParsed: boolean,
|};

type PlaybackPerformance = {
  fatalErrorMsg: string,
  firstLevelFetchTime: number,
  firstFragmentParsedTime: number,
  levelWatchDurationsMs: Array<{ duration: number, lastStartSegmentTime: number, level: number }>,
  hasFatalError: boolean,
  numStalls: number,
  rebufferingDurationMs: number,
  videoCreatedTime: number,
};

const BITS_IN_KILOBIT = 1000;
const MILLISECONDS_DIFF_THRESHOLD = 300;
const MILLISECONDS_IN_SECONDS = 1000;
const MILLISECONDS_INTERVAL = 250;

// Taken from https://github.com/pinterest/gestalt/blob/master/packages/gestalt/src/Video.js
const isNewSource = (oldSource: Source, newSource: Source): boolean => {
  if (typeof oldSource !== typeof newSource) {
    // If the source type changed from string to Array
    // or vice versa, we have a new source
    return true;
  }
  if (Array.isArray(newSource)) {
    if (oldSource.length !== newSource.length) {
      // If the sources are both an Array, and the lengths
      // do not match we evaluate as a new source
      return true;
    }
    // If the sources are both an Array and the same length,
    // verify every element stayed the same
    return newSource.some(
      (source, index) =>
        !Array.isArray(oldSource) ||
        source.type !== oldSource[index].type ||
        source.src !== oldSource[index].src,
    );
  }
  // If the sources are both a string, simply compare the new with the old
  return newSource !== oldSource;
};

/*
 * Helper to return the current unix time in milliseconds.
 */
function currentUnixTimeInMs(): number {
  return new Date().getTime();
}

/**
 * Wrapper around the gestalt Video component that provides additional functionality for HLS.
 * Assumes checks have been done and an an HLS playlist is provided as `src`
 */
export default class HlsVideo extends React.PureComponent<Props, State> {
  state = {
    canPlayVideo: false,
    isManifestParsed: false,
  };

  componentDidMount() {
    this.playbackPerformance.videoCreatedTime = currentUnixTimeInMs();
    // Load the video to hydrate the DOM after a server render
    this.initializeHls();
  }

  componentDidUpdate(prevProps: Props) {
    const { src } = this.props;
    // If the video source changed, reload the video
    if (isNewSource(prevProps.src, src)) {
      this.initializeHls();
    }
  }

  componentWillUnmount() {
    const {
      firstLevelFetchTime,
      firstFragmentParsedTime,
      hasFatalError,
      levelWatchDurationsMs,
      numStalls,
      rebufferingDurationMs,
      videoCreatedTime,
    } = this.playbackPerformance;
    const video = this.videoPlayerRef && this.videoPlayerRef.video;
    if (this.hls && video && levelWatchDurationsMs.length > 0) {
      const levels = this.hls.levels;
      const startupLevelInfo = levels[levelWatchDurationsMs[0].level];

      // end the final level durations segment
      const currentVideoTime = video.currentTime * MILLISECONDS_IN_SECONDS;
      levelWatchDurationsMs[levelWatchDurationsMs.length - 1].duration +=
        currentVideoTime -
        levelWatchDurationsMs[levelWatchDurationsMs.length - 1].lastStartSegmentTime;

      // sum of bit rate per second * duration (in seconds)
      const sumBitRateDuration = levelWatchDurationsMs.reduce((currSum, levelDuration) => {
        const bitRate = levelDuration.level * levels[levelDuration.level].bitrate;
        return currSum + (bitRate * (levelDuration.duration || 0)) / MILLISECONDS_IN_SECONDS;
      }, 0);
      const sumDurationsMs = levelWatchDurationsMs.reduce(
        (currSum, levelDuration) => currSum + (levelDuration.duration || 0),
        0,
      );

      track(PCONST.thrift.EventType.VIDEO_PLAYBACK_PERFORMANCE, {
        eventData: {
          videoPerformanceData: {
            // time between getting first levels information and starting playback
            startupTimeMs: firstFragmentParsedTime - firstLevelFetchTime,
            // time between when HlsVideo mounts and starting playback
            startupLatencyMs: firstFragmentParsedTime - videoCreatedTime,
            nativeVideoDurationMs: video.duration * MILLISECONDS_IN_SECONDS,
            startupVariantKbps: startupLevelInfo.bitrate / BITS_IN_KILOBIT,
            startupPlayerWidth: video.clientWidth,
            startupPlayerHeight: video.clientHeight,
            startupVariantWatchedDurationMs: levelWatchDurationsMs[0].duration,
            startupVariantWidth: startupLevelInfo.width,
            isCellular: false,
            fatalError: hasFatalError,
            averageVideoKbps: sumBitRateDuration / (sumDurationsMs / MILLISECONDS_IN_SECONDS),
            overallWatchedDurationMs: sumDurationsMs,
            numberOfStalls: numStalls,
            rebufferRate: rebufferingDurationMs / sumDurationsMs,
          },
        },
      });
    }

    this.destroyHls();
  }

  setVideoPlayerRef = (ref: ElementRef<typeof Video> | null): void => {
    const { setVideoRef } = this.props;
    this.videoPlayerRef = ref;

    if (setVideoRef) {
      setVideoRef(ref);
    }

    if (this.videoPlayerRef && this.videoPlayerRef.video) {
      // Adding this check specifically for Safari. It seems like there
      // is a race condition that occurs where we attempt to play
      // the video before it's ready, which causes an AbortError.
      this.videoPlayerRef.video.addEventListener('canplay', () => {
        this.setState({ canPlayVideo: true });
      });
    }
  };

  currentVideoTime: number = 0;
  hls: ?Hls = null;
  videoPlayerRef: ElementRef<typeof Video> | null;
  videoTimeIntervalId: ?IntervalID;

  // Need to keep track of some timings for HLS playback performance logging
  // https://paper.dropbox.com/doc/Client-Video-Playback-Performance-KPIs-and-Logging-necSORhFFjyCKKfr5dLMm
  playbackPerformance: PlaybackPerformance = {
    fatalErrorMsg: '',
    firstLevelFetchTime: 0,
    firstFragmentParsedTime: 0,
    hasFatalError: false,
    levelWatchDurationsMs: [],
    numStalls: 0,
    rebufferingDurationMs: 0,
    videoCreatedTime: 0,
  };

  initializeHls = () => {
    this.destroyHls();

    const { src, hlsConfig } = this.props;
    const hls = new Hls(hlsConfig);
    // const hls = new Hls({ ...hlsConfig, debug: true });

    hls.loadSource(src);
    if (!!this.videoPlayerRef) {
      hls.attachMedia(this.videoPlayerRef.video);
    }

    /* ------------------ HLS specific event listeners ------------------ */
    // https://github.com/video-dev/hls.js/blob/master/docs/API.md
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.setState({ isManifestParsed: true });
      this.startVideoWatchTimings();
    });

    hls.on(Hls.Events.FRAG_PARSED, (event, data) => {
      if (!this.playbackPerformance.firstFragmentParsedTime) {
        this.playbackPerformance.firstFragmentParsedTime = currentUnixTimeInMs();
      }
    });

    hls.on(Hls.Events.LEVEL_LOADING, (event, data) => {
      if (!this.playbackPerformance.firstLevelFetchTime) {
        this.playbackPerformance.firstLevelFetchTime = currentUnixTimeInMs();
      }
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.fatal) {
        this.playbackPerformance.hasFatalError = true;
        this.playbackPerformance.fatalErrorMsg = data.details;
      }

      if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
        this.playbackPerformance.numStalls += 1;
        if (data.buffer) {
          this.playbackPerformance.rebufferingDurationMs += data.buffer * MILLISECONDS_IN_SECONDS;
        }
      }
    });

    this.hls = hls;
  };

  destroyHls = () => {
    const { hls } = this;
    if (hls) {
      hls.destroy();
    }
    window.clearInterval(this.videoTimeIntervalId);
  };

  // Going to check video time at a set interval rather than listen to the time update event, as it can
  // vary between being fired off 1-4 times per second.
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/timeupdate_event
  startVideoWatchTimings = () => {
    window.clearInterval(this.videoTimeIntervalId); // Clear the previous timer (if any)
    this.videoTimeIntervalId = window.setInterval(() => {
      if (
        !this.hls ||
        this.hls.currentLevel <= 0 ||
        !this.videoPlayerRef ||
        !this.videoPlayerRef.video
      ) {
        return;
      }

      const videoTime = this.videoPlayerRef.video.currentTime * MILLISECONDS_IN_SECONDS;

      const { levelWatchDurationsMs } = this.playbackPerformance;
      // No levels logged yet; initialize the first one
      if (!levelWatchDurationsMs.length) {
        levelWatchDurationsMs.push({
          level: this.hls.currentLevel,
          lastStartSegmentTime: videoTime, // should this be 0 instead?
          duration: 0,
        });
      } else if (
        this.hls.currentLevel !== levelWatchDurationsMs[levelWatchDurationsMs.length - 1].level
      ) {
        // Level switch has happened; end the previous time and kick off a new one
        const lastIdx = levelWatchDurationsMs.length - 1;
        levelWatchDurationsMs[lastIdx].duration +=
          this.currentVideoTime - levelWatchDurationsMs[lastIdx].lastStartSegmentTime;
        levelWatchDurationsMs.push({
          level: this.hls.currentLevel,
          lastStartSegmentTime: videoTime,
          duration: 0,
        });
      } else if (
        videoTime < this.currentVideoTime ||
        videoTime - this.currentVideoTime > MILLISECONDS_DIFF_THRESHOLD
      ) {
        // either a seek forward or backward has happened
        const lastIdx = levelWatchDurationsMs.length - 1;
        levelWatchDurationsMs[lastIdx].duration +=
          this.currentVideoTime - levelWatchDurationsMs[lastIdx].lastStartSegmentTime;
        levelWatchDurationsMs[lastIdx].lastStartSegmentTime = videoTime;
      }

      this.currentVideoTime = videoTime;
    }, MILLISECONDS_INTERVAL);
  };

  render() {
    const { playing, hlsConfig, setVideoRef, src, ...otherProps } = this.props;
    const { canPlayVideo, isManifestParsed } = this.state;

    return (
      <Video
        {...otherProps}
        ref={this.setVideoPlayerRef}
        playing={isManifestParsed && canPlayVideo && playing}
        src={src}
      />
    );
  }
}
